import React, { FC, ReactNode } from 'react'

import CloseIcon from '@Assets/SVG/Close'
import { Button } from '@Components/Button'
import { SVGIcon } from '@Components/Icons'
import { ErrorContent } from '@Components/Modal/Dialog/ErrorContent'
import {
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogWrapper,
} from '@Components/Modal/Dialog/styledElements'
import { SuccessContent } from '@Components/Modal/Dialog/SuccessContent'
import { useTranslation } from '@Globals/Translations'

export interface DialogProps {
  ID?: string | null
  button?: 'close' | 'okay'
  children?: ReactNode
  easyClose?: boolean
  hideCloseButton?: boolean
  maxWidth?: string
  onHide?: () => void
  title?: string
  variant?: 'default' | 'error' | 'success' | 'sadError'
}

export type ContentProps = Pick<DialogProps, 'children' | 'title' | 'variant'>

const Content: FC<ContentProps> = ({ children, variant, ...props }) => {
  if (variant === 'success') {
    return <SuccessContent {...props}>{children}</SuccessContent>
  }
  if (variant === 'error' || variant === 'sadError') {
    return <ErrorContent {...props} variant={variant}>{children}</ErrorContent>
  }
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>
}

export const Dialog: FC<DialogProps> = ({
  ID = null,
  button,
  children,
  hideCloseButton = false,
  maxWidth = '500px',
  onHide,
  title,
  variant = 'default',
}: DialogProps) => {
  const { t } = useTranslation()

  const buttonText = {
    close: t('common:CLOSE'),
    okay: t('common:OKAY'),
  }

  const dialogWrapperID = ID ? `dialog-wrapper-${ID}` : ''
  const dialogBodyID = ID ? `dialog-body-${ID}` : ''

  return (
    <DialogWrapper
      {...(dialogWrapperID ? { id: dialogWrapperID } : {})}
      onClick={e => e.stopPropagation()}
    >
      {!hideCloseButton && (
        <DialogCloseButton aria-label={t('common:CLOSE')} onClick={onHide}>
          <SVGIcon icon={CloseIcon} size="14px" />
        </DialogCloseButton>
      )}
      <DialogBody
        {...(dialogBodyID ? { id: dialogBodyID } : {})}
        hideCloseButton={hideCloseButton}
        maxWidth={maxWidth}
      >
        <Content title={title} variant={variant}>{children}</Content>
      </DialogBody>
      {button && (
        <DialogFooter>
          <Button color="dark" onClick={onHide} type="button" variant="outlined">
            {buttonText[button]}
          </Button>
        </DialogFooter>
      )}
    </DialogWrapper>
  )
}
